/**
 * Every tunable constant in one place. docs/TUNING_GUIDE.md explains each knob,
 * what moving it does, and how to check the effect with `npm run sim`.
 *
 * RULES_VERSION: bump whenever a change here (or in engine/scoring/adaptive/
 * content) would make an old run replay differently. The server rejects runs
 * whose rulesVersion differs from its own.
 *
 * Adding GameConfig.paceScale ("More time", docs/GAME_DESIGN.md § 4.1) did not
 * bump it: the default (1) replays every existing run exactly as before, and a
 * relaxed run is told apart by its session (stored server-side and signed into
 * the ticket), not by the rules version.
 */
import type { GameConfig } from './types.ts';

/**
 * 2 (2026-09-24): CAPS placement fixes changed what several templates generate
 * (explicit × in Grades 4–6, Grade 7 negatives only added/subtracted, like terms
 * moved to Grade 8, conventional '− □' boxes), so v1 runs no longer replay.
 * The relaxed-pace mode (GameConfig.paceScale, default 1) did not need a bump.
 */
export const RULES_VERSION = 2;
export const LEVEL_COUNT = 24;
export const RATING_MAX = 1000;
export const LEVEL_WIDTH = RATING_MAX / LEVEL_COUNT; // ≈ 41.67 rating points per level

export const DEFAULT_GAME_CONFIG: GameConfig = {
  wellRows: 10, // block spawns in row 0; game over when bricks reach wellRows - 1
  stepsPerRow: 4, // gravity moves in quarter-row steps (Tetris-like discrete motion)
  correctResolveMs: 600, // drop + layer break
  wrongResolveMs: 2000, // drop + turn to brick + show the correct answer
  timeoutResolveMs: 2000, // lock + turn to brick + show the correct answer
  levelBannerMs: 1800,
  checkpointSize: 10, // minimum answered problems before re-evaluating the level
  paceScale: 1, // fall-time multiplier; RELAXED_PACE_SCALE in "More time" runs
};

/**
 * "More time" (relaxed pace, docs/GAME_DESIGN.md § 4.1): an accessibility mode
 * where every block takes this many times as long to fall. Relaxed runs are
 * stored and move the player's level as usual, but are never ranked.
 */
export const RELAXED_PACE_SCALE = 2;

/**
 * The GameConfig override for a run's pace mode. Client, server and the
 * offline demo all derive it here, so a run replays under the pace it was
 * played at.
 */
export function paceConfig(relaxed: boolean): Partial<GameConfig> {
  return relaxed ? { paceScale: RELAXED_PACE_SCALE } : {};
}

/** Adaptive difficulty (see docs/ADAPTIVE.md for the formulas). */
export const ADAPTIVE = {
  // Speed score S = clamp01((T_SLOW - T) / (T_SLOW - T_FAST)), T = median(responseMs / parTimeMs) of correct answers
  T_FAST: 0.45,
  T_SLOW: 1.4,
  T_NO_CORRECT: 2.0,
  // Consistency C = C_TIME_WEIGHT * (1 - clamp01(cv(τ) / CV_MAX)) + (1 - C_TIME_WEIGHT) * longestCorrectRun / n
  CV_MAX: 0.9,
  C_TIME_WEIGHT: 0.5,
  // Performance P = W_A*A + W_S*S*A + W_C*C*A   (weights sum to 1). Placement is by accuracy (+ a little
  // consistency), NOT speed: with speed in P, fast players were pushed up the steep part of their curve and
  // careless-slow ones parked low, so efficiency couldn't rank them (sim, 2026-09-24). Speed still drives
  // jump size (confidence), under-placement and RatedScore.
  W_A: 0.9,
  W_S: 0,
  W_C: 0.1,
  // Target performance: at P = P_TARGET the rating does not move (≈ 80% accuracy: the "flow zone")
  P_TARGET: 0.78,
  // Mastery gate: never move up with accuracy below this
  A_MIN_UP: 0.7,
  // Rating step sizes (rating points) for a fully positive / fully negative normalized delta
  K_UP: 55,
  K_DOWN: 60,
  // Confidence multiplier for upward moves: 1 + (JUMP_MAX - 1) * confidence^2
  JUMP_MAX: 5,
  // confidence = ramp(A, CONF_A0, 1) * ramp(S, CONF_S0, CONF_S1) * ramp(C, CONF_C0, CONF_C1)
  CONF_A0: 0.8,
  CONF_S0: 0.35,
  CONF_S1: 0.8,
  CONF_C0: 0.35,
  CONF_C1: 0.8,
  // Safety rail: never drop more than this many levels at one checkpoint
  MAX_DROP_LEVELS: 2,
  // Hysteresis (rating points) around level boundaries to prevent oscillation
  HYSTERESIS: 14,
  // A timeout only counts against accuracy when the block allowed ≥ PACE_TIMEOUT_PAR × par; faster falls are
  // the pace ramp outrunning the player, which ends runs but must not make the algebra easier.
  PACE_TIMEOUT_PAR: 1.5,
  // Under-placement: correct answers this fast (median τ ≤ T_UNDERPLACED) mean the content is too easy even
  // when accuracy is modest. With A ≥ A_UNDERPLACED the mastery gate is bypassed and d is at least D_UNDERPLACED,
  // so deliberately missing answers can't hold a strong player on easy levels.
  T_UNDERPLACED: 0.55,
  A_UNDERPLACED: 0.5,
  D_UNDERPLACED: 0.4,
  UNDERPLACED_MIN_LEVELS: 1, // … and the rating rises by at least this many levels
  // Jump probation: after a jump of ≥ PROBATION_MIN_JUMP levels, PROBATION_MISSES misses (wrong, or a
  // non-pace timeout) within the first PROBATION_ANSWERS answers cancel the jump.
  PROBATION_MIN_JUMP: 2,
  PROBATION_ANSWERS: 5,
  PROBATION_MISSES: 3,
  // Returning players start this many levels below their saved rating
  WARM_START_LEVELS: 1.5,
  // Saved rating after a run = clamp(finalRating, peak - SAVE_PEAK_MARGIN_LEVELS * LEVEL_WIDTH, peak),
  // peak = highest DEMONSTRATED rating (never a level the player was merely promoted to)
  SAVE_PEAK_MARGIN_LEVELS: 1.5,
} as const;

/** Fall pace (see docs/ADAPTIVE.md § Pace). fallTimeMs = parTimeMs × slack(level) × runPace × levelPace × ratingPace */
export const PACE = {
  SLACK_FIRST: 3.2, // slack at level 1 (lots of thinking room)
  SLACK_LAST: 2.2, // slack at the top level
  // Run-long ramp (Tetris-style): runPace = max(RUN_FLOOR, 1 / (1 + answered / RUN_HALF))
  RUN_HALF: 60,
  RUN_FLOOR: 0.45,
  // In-level ramp: levelPace = max(LEVEL_FLOOR, 1 - LEVEL_RAMP * answeredAtLevel)
  LEVEL_RAMP: 0.015,
  LEVEL_FLOOR: 0.85,
  // Position within the level's rating band: ratingPace = 1 - RATING_TWEAK * frac
  RATING_TWEAK: 0.08,
  MIN_FALL_MS: 3500,
  MAX_FALL_MS: 60000,
} as const;

/** Scoring (see docs/SCORING.md for rationale). */
export const SCORING = {
  // RawScore (arcade)
  RAW_CORRECT: 100, // × D × (1 + remainingFraction) × streakMult
  RAW_STREAK_STEP: 0.1, // streakMult = 1 + STEP × min(streak - 1, RAW_STREAK_CAP)
  RAW_STREAK_CAP: 10,
  RAW_LAYER_BREAK: 50, // × D
  RAW_CLEAN_FLOOR: 20, // × D, correct answer with an empty stack
  RAW_LEVEL_UP: 250, // × D(new level) per level gained
  // RatedScore (handicap efficiency): Run Rated = max(0, round(UNIT × Σ m·w·c / (Σ m·t̂ + PRIOR_N0)))
  RATED_UNIT: 1000, // scale: ≈ 1000 × credited par-work per par-unit of time
  RATED_PRIOR_N0: 6, // prior: N0 par-units of time with no credit, so short runs shrink toward 0
  RATED_TAU_FLOOR: 0.35, // t̂ = clamp(response / par, FLOOR, CAP): no extra credit for answering faster than this
  RATED_TAU_CAP: 2, // … and a single slow answer / timeout costs at most this much time
  RATED_WRONG_LAMBDA: 0.6, // credit c = -λ for wrong/timeout (c = 1 for correct)
  RATED_D_ETA: 0.15, // w(D) = D^η : harder bands earn a little more, not proportionally more
  // Relevance weight m = clamp(1 - MU·max(0, gap - GAP_FREE), M_FLOOR, 1),
  // gap = level(anchorRating) - problemLevel, anchorRating = max(referenceRating, run peak rating)
  RATED_MU: 0.34, // m reaches 0 five levels below the anchor level (with GAP_FREE 2)
  RATED_GAP_FREE: 2,
  RATED_M_FLOOR: 0, // answers far below your demonstrated level don't count at all (farming, tanking, placement climb)
} as const;

/** Server-side anti-cheat thresholds (see docs/BACKEND.md § Anti-cheat). */
export const ANTICHEAT = {
  MIN_HUMAN_MS_BASE: 400, // minimum plausible response: base + per prompt char
  MIN_HUMAN_MS_PER_CHAR: 25,
  TOO_FAST_FLAG_COUNT: 3, // this many too-fast correct answers → flag
  TOO_FAST_REJECT_FRACTION: 0.3, // this fraction of answers too fast → reject
  ROBOTIC_MIN_ANSWERS: 15,
  ROBOTIC_CV: 0.05, // cv of τ among correct answers below this → flag
  WALL_CLOCK_SLACK_MS: 5000, // engine duration may not exceed wall-clock session time + slack
  // Relevance-window checks judge only the answers that count for RatedScore
  // (relevance m > 0 at the run's anchor), and only once there are this many.
  // 10, not more: a perfect bot that climbs from level 1 and quits after 35
  // answers has only ~15 answers left in the window, yet scores ~2.7× honest.
  RELEVANT_MIN_ANSWERS: 10,
  NEAR_PERFECT_ACCURACY: 0.95, // accuracy at or above this in the window …
  NEAR_PERFECT_MEDIAN_TAU: 0.5, // … with median τ of its correct answers at or below this → flag ("perfect-superhuman")
  NEAR_PERFECT_SUSTAINED_ANSWERS: 30, // … or over at least this many answers in the window, at any pace
  // Credited speed-up in the window: correct answers ÷ Σ t̂ (τ clamped as RatedScore does) at or above this → flag.
  // Honest sim runs top out near 1.5 over a whole run; accuracy × speed this high is only reached by always-right fast play.
  MAX_CREDITED_SPEEDUP: 2,
  // Impossible rate: Σ parTimeMs of correct answers ÷ engine duration above this → flag
  MAX_PAR_SPEEDUP: 4,
  RATE_MIN_ANSWERS: 20, // … only judged over at least this many answers
  SESSION_TTL_MS: 3 * 60 * 60 * 1000,
} as const;
